import {
  useCallback, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { createUuid } from '~/_shared/utils/createUuid';
import { getAddressFromLatLng } from '~/_shared/utils/geolocation/geolocation.helpers';
import { convertMetersToUnit } from '~/_shared/utils/unitSystem/unitSystem.helpers';
import { AppErrorType } from '~/appError/appErrorType.enum';
import { getClosestDistanceFromCenterToBoundsEdge } from '~/places/places.helpers';
import { PlacesSearchResultsRankingPreference } from '~/places/places.repository';
import { usePlacesErrors } from '~/sidebar/sidebarApps/mapTools/placesTool/hooks/usePlacesErrors';
import { getNextPlacesSearchColor } from '~/sidebar/sidebarApps/mapTools/placesTool/placesTool.helpers';
import { openMapTool } from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import {
  useMapComponentCenterSelector, useMapComponentLastBoundsSelector,
} from '~/store/frontendState/mapComponent/mapComponent.selectors';
import { mapSettingsPlacesAddSearch } from '~/store/mapSettings/places/mapSettingsPlaces.actionCreators';
import { useMapSettingsPlacesSearchesSelector } from '~/store/mapSettings/places/mapSettingsPlaces.selectors';
import { useMapSettingsSettingsUnitSystemSelector } from '~/store/mapSettings/settings/mapSettingsSettings.selectors';
import { createAppError } from '~/store/modal/modal.actionCreators';

export const useMapSearchPlaces = () => {
  const dispatch = useDispatch();
  const mapLastBounds = useMapComponentLastBoundsSelector();
  const mapCenter = useMapComponentCenterSelector();
  const placesSearches = useMapSettingsPlacesSearchesSelector();
  const unitSystem = useMapSettingsSettingsUnitSystemSelector();
  const { overLimitMessage } = usePlacesErrors();

  const [isLoading, setIsLoading] = useState(false);

  const addNewPlaceSearch = useCallback(async (searchPhrase: string) => {
    if (overLimitMessage) {
      dispatch(
        createAppError({
          type: AppErrorType.General,
          title: overLimitMessage.title,
          errorTitle: overLimitMessage.message,
        })
      );
      return;
    }

    if (mapLastBounds) {
      setIsLoading(true);

      let address = '';
      try {
        const geolocationFromLatLng = await getAddressFromLatLng(
          mapCenter.lat,
          mapCenter.lng,
        );
        if (geolocationFromLatLng.address) {
          address = geolocationFromLatLng.address;
        }
      }
      catch {
        address = `${mapCenter.lat}, ${mapCenter.lng}`;
      }

      dispatch(mapSettingsPlacesAddSearch({
        newSearch: {
          id: createUuid(),
          preference: PlacesSearchResultsRankingPreference.Relevance,
          text: searchPhrase,
          center: {
            name: address,
            latLng: {
              lat: mapCenter.lat,
              lng: mapCenter.lng,
            },
          },
          distance: {
            value: Math.round(convertMetersToUnit(getClosestDistanceFromCenterToBoundsEdge(
              { lat: mapCenter.lat, lng: mapCenter.lng },
              mapLastBounds.bounds,
            ), unitSystem)),
            unit: unitSystem,
          },
          color: getNextPlacesSearchColor(placesSearches.map(search => search.color)),
        },
        zoomTo: true,
      }));
      dispatch(openMapTool(MapTool.Places));
    }
    setIsLoading(false);
  }, [dispatch, mapCenter.lat, mapCenter.lng, mapLastBounds, overLimitMessage, placesSearches, unitSystem]);

  return {
    addNewPlaceSearch,
    isLoading,
  };
};
